import React from 'react';
import {
  REVIEW_LENSES,
  ReviewCapabilityFields,
  ReviewCheckSpec,
} from '../../../api/structs/pipeline';
import { AutoResizeTextarea } from '../../ui';
import { detailChip, detailChipOn } from './reviewerDetailStyles';
import {
  CapabilitySwitch,
  InheritCaption,
  InheritedStatus,
  fieldCaption,
  fieldLabel,
  groupHeading,
} from './reviewFormStyles';
import { ResolvedReviewCapabilities } from './resolveReviewCapabilities';

/** The engine's guidance cap (`types.MaxReviewGuidanceChars`). */
export const MAX_REVIEW_GUIDANCE_CHARS = 2000;

/**
 * The one rule almost every pipeline eventually needs, offered as a starter so
 * nobody has to remember the wording that actually works on a judge.
 */
export const COLLAGE_STARTER_CHECK =
  'One continuous frame: no collage, split panels, grids, borders or added text unless the prompt asks for it';

export interface ReviewCapabilitiesFieldsProps {
  value: ReviewCapabilityFields;
  inherited: ResolvedReviewCapabilities;
  /** What the layer above is called, for the captions ("your account", …). */
  inheritsFrom: string;
  /** The pair judge only exists on a generator that makes a closing frame. */
  showPairJudge: boolean;
  /** Whether `inherited` is actually known yet — see `InheritCaption`. */
  inheritedStatus?: InheritedStatus;
  /**
   * A per-instance prefix for the DOM `id`s below. The canvas inspector is
   * mounted TWICE (the desktop rail and the mobile sheet), so a static id would
   * be duplicated in one document and every `<label for>` would point at
   * whichever copy the browser found first.
   */
  idPrefix: string;
  onChange: (next: ReviewCapabilityFields) => void;
}

const yesNo = (on: boolean) => (on ? 'on' : 'off');

/**
 * Nine of the ten capability fields — what the judge is SHOWN and what it is
 * told to rule on. (The tenth, `pair`, is a model reference and lives with the
 * other tiers in `ReviewModelsAndGates`.)
 *
 * Every control writes its own key and nothing else, and every one can be
 * unset again: an absent key means "inherit", which is a different statement
 * from `false`, and only the Reset button can express it.
 */
const ReviewCapabilitiesFields: React.FC<ReviewCapabilitiesFieldsProps> = ({
  value,
  inherited,
  inheritsFrom,
  showPairJudge,
  inheritedStatus,
  idPrefix,
  onChange,
}) => {
  const [guidanceFullscreen, setGuidanceFullscreen] = React.useState(false);

  // Escape closes THIS overlay and stops there — the same pattern
  // ReviewerPromptSection uses for its read-only fullscreen. A CAPTURE
  // listener runs before any bubble-phase one, which is what makes stopping
  // it possible before a surface further out (the canvas, a parent modal)
  // also reacts to the same keypress.
  React.useEffect(() => {
    if (!guidanceFullscreen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      event.stopImmediatePropagation();
      setGuidanceFullscreen(false);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [guidanceFullscreen]);

  const set = <K extends keyof ReviewCapabilityFields>(
    key: K,
    next: ReviewCapabilityFields[K]
  ) => onChange({ ...value, [key]: next });

  const unset = (key: keyof ReviewCapabilityFields) => {
    const next = { ...value };
    delete next[key];
    onChange(next);
  };

  const toggleBoolean = (
    key: 'story' | 'descriptions' | 'attempt_history' | 'cast' | 'pair_judge'
  ) => set(key, !(value[key] ?? inherited[key]));

  const booleanRow = (
    key: 'story' | 'descriptions' | 'attempt_history' | 'cast' | 'pair_judge',
    label: string,
    help: string
  ) => (
    <div>
      <CapabilitySwitch
        field={key}
        label={label}
        checked={value[key] ?? inherited[key]}
        onToggle={() => toggleBoolean(key)}
      />
      <InheritCaption
        field={key}
        from={inheritsFrom}
        status={inheritedStatus}
        inherits={yesNo(inherited[key])}
        isSet={value[key] !== undefined}
        onReset={() => unset(key)}
      />
      <p className={fieldCaption}>{help}</p>
    </div>
  );

  const lenses = (value.lenses as string[] | undefined) ?? [];
  const toggleLens = (lens: string) => {
    const next = lenses.includes(lens)
      ? lenses.filter((entry) => entry !== lens)
      : [...lenses, lens];
    if (next.length === 0) unset('lenses');
    else set('lenses', next);
  };

  const checks: ReviewCheckSpec[] = value.fixed_checks ?? [];
  const writeChecks = (next: ReviewCheckSpec[]) => {
    if (next.length === 0) unset('fixed_checks');
    else set('fixed_checks', next);
  };

  const guidance = value.guidance ?? '';
  const guidanceEditor = (rows: number, testId: string, id?: string, ariaLabel?: string) => (
    <AutoResizeTextarea
      id={id}
      aria-label={ariaLabel}
      data-testid={testId}
      className="w-full text-sm"
      rows={rows}
      maxLength={MAX_REVIEW_GUIDANCE_CHARS}
      placeholder="Anything the judge should keep in mind for this pipeline."
      value={guidance}
      onChange={(event) => {
        const text = event.target.value;
        if (text === '') unset('guidance');
        else set('guidance', text);
      }}
    />
  );

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <h5 className={groupHeading}>What the judge is shown</h5>
        {booleanRow('story', 'The run’s story', 'The premise and the scene’s place in it.')}
        {booleanRow(
          'descriptions',
          'Reference descriptions',
          'Each reference image is labelled with its saved description.'
        )}
        {booleanRow(
          'attempt_history',
          'Earlier attempts',
          'What this checkpoint already produced and why it was rejected, this run.'
        )}
        {booleanRow(
          'cast',
          'Cast',
          'The judges see every character’s reference picture and anchors and check identity'
        )}
        {showPairJudge &&
          booleanRow(
            'pair_judge',
            'Read both frames together',
            'A second judge reads the scene’s opening and closing frame as a pair.'
          )}

        <div>
          <label className={fieldLabel} htmlFor={`${idPrefix}-scene-scope`}>
            Neighbouring scenes
          </label>
          <select
            id={`${idPrefix}-scene-scope`}
            data-testid="review-scene-scope"
            className="select w-full"
            value={value.scene_scope === undefined ? '' : String(value.scene_scope)}
            onChange={(event) => {
              if (event.target.value === '') unset('scene_scope');
              else set('scene_scope', Number(event.target.value));
            }}
          >
            <option value="">inherit</option>
            <option value="0">0 — this scene alone</option>
            <option value="1">1 — the previous scene</option>
            <option value="2">2 — the previous two scenes</option>
            <option value="3">3 — the story so far</option>
          </select>
          <InheritCaption
            field="scene_scope"
            from={inheritsFrom}
            status={inheritedStatus}
            inherits={String(inherited.scene_scope)}
            isSet={value.scene_scope !== undefined}
            onReset={() => unset('scene_scope')}
          />
        </div>

        <div>
          <div className="flex items-center gap-2">
            <label className={`${fieldLabel} mb-0`} htmlFor={`${idPrefix}-guidance`}>
              Guidance
            </label>
            <button
              type="button"
              className="btn btn-sm btn-ghost ml-auto !px-1.5 !py-0 font-mono text-[10px]"
              data-testid="review-guidance-fullscreen"
              title="Write it full screen"
              aria-label="Write the guidance full screen"
              onClick={() => setGuidanceFullscreen(true)}
            >
              ⛶
            </button>
          </div>
          {guidanceEditor(3, 'review-guidance', `${idPrefix}-guidance`)}
          <InheritCaption
            field="guidance"
            from={inheritsFrom}
            status={inheritedStatus}
            inherits={inherited.guidance || '—'}
            isSet={value.guidance !== undefined}
            onReset={() => unset('guidance')}
          />
          <p className={fieldCaption}>
            {guidance.length}/{MAX_REVIEW_GUIDANCE_CHARS}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <h5 className={groupHeading}>What it rules on</h5>

        <div className="space-y-2">
          {checks.map((check, index) => (
            <div key={index} className="flex items-start gap-2">
              <input
                type="text"
                className="input flex-1 text-sm"
                data-testid={`review-fixed-check-${index}`}
                aria-label={`Fixed check ${index + 1}`}
                maxLength={300}
                value={check.check}
                placeholder="Something the judge must verify verbatim"
                onChange={(event) =>
                  writeChecks(
                    checks.map((entry, position) =>
                      position === index ? { ...entry, check: event.target.value } : entry
                    )
                  )
                }
              />
              <label className={`${fieldCaption} flex items-center gap-1 pt-2`}>
                <input
                  type="checkbox"
                  data-testid={`review-fixed-check-required-${index}`}
                  checked={Boolean(check.required)}
                  onChange={(event) =>
                    writeChecks(
                      checks.map((entry, position) =>
                        position === index
                          ? event.target.checked
                            ? { ...entry, required: true }
                            : { check: entry.check }
                          : entry
                      )
                    )
                  }
                />
                required
              </label>
              <button
                type="button"
                className="btn btn-sm btn-ghost !px-1.5 !py-0"
                data-testid={`review-fixed-check-remove-${index}`}
                aria-label={`Remove fixed check ${index + 1}`}
                onClick={() => writeChecks(checks.filter((_, position) => position !== index))}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            data-testid="review-fixed-check-add"
            onClick={() =>
              writeChecks([
                ...checks,
                { check: checks.length === 0 ? COLLAGE_STARTER_CHECK : '' },
              ])
            }
          >
            + Add
          </button>
          <InheritCaption
            field="fixed_checks"
            from={inheritsFrom}
            status={inheritedStatus}
            inherits={
              inherited.fixed_checks.length > 0 ? `${inherited.fixed_checks.length} check(s)` : '—'
            }
            isSet={value.fixed_checks !== undefined}
            onReset={() => unset('fixed_checks')}
          />
          <p className={fieldCaption}>
            Checked verbatim on every candidate, on top of the criteria the planner wrote. Required
            checks never pass on an uncertain answer.
          </p>
        </div>

        <div>
          <span className={fieldLabel}>Lenses</span>
          <div className="flex flex-wrap gap-1">
            {REVIEW_LENSES.map((lens) => {
              const on = lenses.includes(lens);
              return (
                <button
                  key={lens}
                  type="button"
                  data-testid={`review-lens-${lens}`}
                  aria-pressed={on}
                  className={on ? detailChipOn : detailChip}
                  onClick={() => toggleLens(lens)}
                >
                  {lens}
                </button>
              );
            })}
          </div>
          <InheritCaption
            field="lenses"
            from={inheritsFrom}
            status={inheritedStatus}
            inherits={inherited.lenses.length > 0 ? inherited.lenses.join(', ') : '—'}
            isSet={value.lenses !== undefined}
            onReset={() => unset('lenses')}
          />
        </div>
      </div>

      {guidanceFullscreen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Guidance"
          className="fixed inset-0 z-[230] flex flex-col bg-black/95 p-4"
          data-testid="review-guidance-overlay"
        >
          <div className="flex items-center gap-2 pb-2">
            <h4 className={groupHeading}>Guidance</h4>
            <button
              type="button"
              className="btn btn-sm btn-ghost ml-auto font-mono text-[10px]"
              data-testid="review-guidance-fullscreen-exit"
              onClick={() => setGuidanceFullscreen(false)}
            >
              ⊠ Close
            </button>
          </div>
          <div className="flex-1 overflow-auto">
            {guidanceEditor(20, 'review-guidance-fullscreen-input', undefined, 'Guidance')}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewCapabilitiesFields;
